import { FormContext, FormHelpText } from "@stormwater-software/lib-form";
import { LoadingIcon } from "@stormwater-software/lib-ui";
import { Tooltip } from "@mui/material";
import { AxiosError } from "axios";
import classnames from "classnames";
import { noop } from "lodash";
import PropTypes from "prop-types";
import React, {
  MouseEventHandler,
  PropsWithChildren,
  SyntheticEvent,
  useContext,
} from "react";
import TinyIconButton from "../../TinyIconButton";

export type SubmitHandlerFn = (
  data: any,
  event: SyntheticEvent,
) => Promise<void>;

/**
 * Generic form submission handler
 */
export const submitHandler = (
  formContext: React.ContextType<typeof FormContext>,
  event: SyntheticEvent,
  callback: SubmitHandlerFn,
  errorMessageText?: string,
) => {
  event.preventDefault();

  if (formContext.isBusy) {
    return false;
  }

  formContext.setBusy(true);

  // reset error state
  formContext.setError(null);

  return formContext
    .validate()
    .then(valid => {
      if (valid) {
        /** @important the callback should reset "busy" state */

        return formContext.getTransformedData();
      } else {
        return Promise.reject({
          message: errorMessageText,
        });
      }
    })
    .then(data => Promise.resolve(callback(data, event)))
    .then((...args) => {
      if (formContext.isMounted.current === true) {
        formContext.setBusy(false);
      }

      return Promise.resolve(...args);
    })
    .catch(getSubmitErrorHandler(formContext.setError, formContext.setBusy));
};

export const getSubmitErrorHandler =
  (setError: (message: string) => void, setBusy: (busy: boolean) => void) =>
  (err: Error | AxiosError | any) => {
    const defaultError = "An unknown error has occurred";

    console.error("getSubmitErrorHandler err:", err);

    setBusy(false);

    // axios error
    if (err && err.response) {
      const { data } = err.response;
      const message = data.message ? data.message : defaultError;

      setError(message);
    }
    // invalid form error
    else if (err && err.message) {
      setError(err.message);
    }
    // unknown error
    else {
      setError(typeof err === "string" ? err : defaultError);
    }

    // don't propagate the error
    // return Promise.reject(err);
  };

export interface IFormActionsProps {
  onCancel?: MouseEventHandler;
  onSubmit: SubmitHandlerFn;
  onDelete?: any;
  small?: boolean;
  showCancel?: boolean;
  showSubmit?: boolean;
  showDelete?: boolean;
  showErrorText?: boolean;
  /**
   * Blocks submission on a condition the form schema cannot express — cross-field rules
   * such as "a grid needs at least 2 columns and 1 row". Additive to the existing
   * busy/validity guard, never a way to force-enable it.
   */
  submitDisabled?: boolean;
  submitText?: string;
  cancelText?: string;
  deleteText?: string;
  errorMessageText?: string;
  nextBtn?: boolean;
  isNextPage?: boolean;
  handleNext?: MouseEventHandler;
  handleBack?: MouseEventHandler;
  submitTooltip?: string;
}

/**
 * Display a row of form-wide user feedback and form action buttons
 *
 * "small" option shows inline icon buttons
 */
const FormActions: React.FC<PropsWithChildren<IFormActionsProps>> = ({
  onCancel = () => {},
  onSubmit,
  onDelete = () => {},
  small = false,
  showCancel = true,
  showSubmit = true,
  showDelete = false,
  submitText = "Ok",
  cancelText = "Cancel",
  deleteText = "Delete",
  errorMessageText = "Invalid form data. Please review errors above.",
  showErrorText = true,
  submitDisabled = false,
  submitTooltip,
  children,
  ...props
}) => {
  const formContext = useContext(FormContext);
  const isBusy = formContext.isBusy;

  return (
    <div
      className={classnames("form-actions", {
        "form-actions-small": small,
        "form-actions-is-busy": isBusy,
      })}
    >
      {formContext.error && showErrorText ? (
        <FormHelpText theme="error" content={formContext.error} center />
      ) : null}

      {small ? (
        <div className="form-actions-row">
          {children}

          {!isBusy && (
            <TinyIconButton
              iconName="close"
              onClick={onCancel}
              className="form-action-secondary"
              title={cancelText}
            />
          )}

          <TinyIconButton
            className="form-action-primary"
            iconClassName={classnames({
              "fa-spin": isBusy,
            })}
            iconName={isBusy ? "spinner" : "check"}
            disabled={isBusy || !formContext.isValid || submitDisabled}
            onClick={(event: SyntheticEvent) =>
              submitHandler(formContext, event, onSubmit, errorMessageText)
            }
            title={submitText}
          />
        </div>
      ) : (
        <div className="form-actions-row">
          {children}

          {props.nextBtn ? (
            <>
              {!props.isNextPage && showCancel && (
                <button
                  className="reversed form-action-secondary"
                  type="button"
                  onClick={onCancel}
                >
                  {cancelText}
                </button>
              )}

              {!props.isNextPage && (
                <button
                  className="primary form-action-primary"
                  type="button"
                  onClick={props.handleNext}
                >
                  Next
                </button>
              )}

              {props.isNextPage && (
                <button
                  className="reversed form-action-secondary"
                  type="button"
                  onClick={props.handleBack}
                >
                  Back
                </button>
              )}

              {props.isNextPage && showSubmit && (
                <Tooltip
                  title={submitDisabled && submitTooltip ? submitTooltip : ''}
                  arrow
                  placement="top"
                  enterDelay={0}
                  enterTouchDelay={0}
                >
                  <span style={{ display: 'inline-block' }}>
                    <button
                      className="primary form-action-primary"
                      type="submit"
                      disabled={isBusy || !formContext.isValid || submitDisabled}
                      onClick={event =>
                        submitHandler(formContext, event, onSubmit, errorMessageText)
                      }
                    >
                      {isBusy ? <LoadingIcon /> : submitText}
                    </button>
                  </span>
                </Tooltip>
              )}
            </>
          ):
          (
            <>
              {showDelete &&
                <button
                  className="warn form-action-secondary"
                  type="button"
                  onClick={onDelete}
                  style={{ float: "left"}}
                >
                  {deleteText}
                </button>
              }
              
              {showCancel && (
                <button
                  className="reversed form-action-secondary"
                  type="button"
                  onClick={onCancel}
                >
                  {cancelText}
                </button>
              )}
    
              {showSubmit && (
                <Tooltip
                  title={submitDisabled && submitTooltip ? submitTooltip : ''}
                  arrow
                  placement="top"
                  enterDelay={0}
                  enterTouchDelay={0}
                >
                  <span style={{ display: 'inline-block' }}>
                    <button
                      className="primary form-action-primary"
                      type="submit"
                      disabled={isBusy || !formContext.isValid || submitDisabled}
                      onClick={event =>
                        submitHandler(formContext, event, onSubmit, errorMessageText)
                      }
                    >
                      {isBusy ? <LoadingIcon /> : submitText}
                    </button>
                  </span>
                </Tooltip>
              )}
            </>
          )
          }
        </div>
      )}
    </div>
  );
};

FormActions.propTypes = {
  onCancel: PropTypes.func.isRequired, // (event) => void
  onSubmit: PropTypes.func.isRequired, // (value, event) => void

  /** Whether to render small icon buttons */
  small: PropTypes.bool,

  showCancel: PropTypes.bool,
  showSubmit: PropTypes.bool,

  submitText: PropTypes.string.isRequired,
  cancelText: PropTypes.string.isRequired,

  showErrorText: PropTypes.bool.isRequired,
  errorMessageText: PropTypes.string.isRequired,
};

FormActions.defaultProps = {
  onCancel: noop,
  small: false,
  showCancel: true,
  showSubmit: true,
  submitText: "Ok",
  cancelText: "Cancel",
  showErrorText: true,
  errorMessageText: "Invalid form data. Please review errors above.",
};

export default FormActions;
